import { pull } from "@aprao/common/src/array/pull.js";
import { makeAutoObservable } from "mobx";

import { createTimelineOverride } from "../createTimelineOverride.js";
import type {
  CustomCostKeys,
  ShapeCustomCost,
  ShapeSitePurchase,
} from "../shapes/ShapeSitePurchase.js";
import { WithNameDelegate } from "./delegates/WithNameDelegate.js";
import { WithProjectionPeriodsDelegate } from "./delegates/WithProjectionPeriodsDelegate.js";
import { WithRelativesSetDelegate } from "./delegates/WithRelativesSetDelegate.js";
import { PricePurchaseFacade } from "./PricePurchaseFacade.js";
import type { NominatorProvidersByKey } from "./RelativesSetFacade.js";

const createCustomCost = (ordinal: number): ShapeCustomCost => ({
  name: `Custom cost ${ordinal}`,
  relatives: {
    absolute: null,
    ratioPrice: null,
  },
  projectionPeriods: [],
  timelineOverride: createTimelineOverride(),
});

export class CustomCostFacade {
  readonly $model;

  readonly _providers;

  constructor(
    model: ShapeCustomCost,
    providers: NominatorProvidersByKey<CustomCostKeys>,
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._providers = providers;
  }

  get relativesSet() {
    return new WithRelativesSetDelegate(this.$model, this._providers);
  }

  get named() {
    return new WithNameDelegate(this.$model);
  }

  get projectionPeriods() {
    return new WithProjectionPeriodsDelegate(this.$model);
  }
}

export class SitePurchaseFacade {
  readonly $model;

  constructor(model: ShapeSitePurchase) {
    makeAutoObservable(this);

    this.$model = model;
  }

  get description() {
    return this.$model.description;
  }

  get pricePurchase() {
    return new PricePurchaseFacade(this.$model.pricePurchase);
  }

  set description(description: string) {
    this.$model.description = description;
  }

  addCustomCost() {
    this.$model.customCosts.push(
      createCustomCost(this.$model.customCosts.length + 1),
    );
  }

  removeCustomCost(cost: CustomCostFacade) {
    pull(this.$model.customCosts, cost.$model);
  }
}
